import axios from 'axios'

class UsageService {
  constructor(apiUrl) {
    this.apiUrl = apiUrl
  }

  /**
   * @author Jorge Perales
   * @description Get plan info, avg tokens per conversation, daily average tokens and usage tokens by month
   * @param {String} clientId 
   * @param {String} year 
   * @param {String} month 
   * @param {String} email 
   * @param {String} token 
   * @returns {Object}
   * @memberof UsageService
   */
  async getUsagePart1Passthrough(year, month, token, botId = null) {
    const query = `year=${year}&month=${month}${botId ? `&botId=${botId}` : ''}`

    let response = await axios.get(`${this.apiUrl}/usage-part1-passthrough?${query}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.data
  }

  /**
   * @author Jorge Perales
   * @description Get conversations with messages by botId
   * @updatedDate 22-02-2026
   * @param {String} token
   * @param {String} botId
   * @returns {Object}
   * @memberof UsageService
   */
  async getConversationsByMonth(token, botId) {
    let userTimezone
    try {
        userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
        userTimezone = 'America/Mexico_City'
    }

    let response = await axios.get(`${this.apiUrl}/conversations-with-messages?botId=${botId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Timezone': userTimezone
      }
    })
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.data
  }

  /**
   * @author Antonio Vazquez
   * @description Get conversations with messages by clientId, year and month, limit and page
   * @param {String} year
   * @param {String} month
   * @param {String} token
   * @param {String} day
   * @param {String} page
   * @param {String} limit
   * @param {String} sort
   * @returns {Object}
   * @memberof UsageService
   */
  async getConversationsByDateAndPageLimit(year, month, token, day, page, limit, sort) {
    let response = await axios.get(
      `${this.apiUrl}/conversations-with-messages-by-page?year=${year}&month=${month}${day ? '&day=' + day : ''}${page ? '&page=' + page : ''}${limit ? '&limit=' + limit : ''}${sort ? '&sort=' + sort : ''}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    )
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.data
  }

  /**
   * @author Brayan Salas
   * @description Search conversations by matching message content
   * @param {String} search
   * @param {String} year
   * @param {String} month
   * @param {String} token
   * @param {String} day
   * @param {String} botId
   * @param {String} page
   * @param {String} limit
   * @param {String} sort
   * @returns {Object}
   * @memberof UsageService
   */
  async searchConversationsByMessage(search, year, month, token, day, botId, page, limit, sort) {
    let response = await axios.get(
      `${this.apiUrl}/search-conversations-by-message?search=${encodeURIComponent(search)}&year=${year}&month=${month}${day ? '&day=' + day : ''}${botId ? '&botId=' + botId : ''}${page ? '&page=' + page : ''}${limit ? '&limit=' + limit : ''}${sort ? '&sort=' + sort : ''}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    )

    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }

    return response.data.data
  }

  /**
   * @author Jorge Perales
   * @description Get daily token usage by clientId, year and month
   * @param {String} year 
   * @param {String} month 
   * @param {String} botId
   * @param {String} token 
   * @returns {Object}
   * @memberof UsageService
   */
  async getDailyTokenUsage(year, month, botId, token) {
    const query = `year=${year}&month=${month}${botId ? `&botId=${botId}` : ''}`
    let response = await axios.get(`${this.apiUrl}/daily-token-usage?${query}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.data
  }

  /**
   * @author Antonio Vazquez
   * @description method to update messages to seen
   * @date 06-11-2025
   * @param {string} conversationId | conversation id
   * @param {string} author | author prefix
   * @returns void
   * @memberof UsageService
   */
  async messagesSeen(conversationId, author) {
      try {
          const response = await axios.put(
              `${this.apiUrl}/messages-seen`,
              { conversationId, author }
          )

          if (response.data.statusCode !== 200) {
              throw new Error(response.data.message)
          }

          return response.data.data
      } catch (e) {
          console.error(`An error occurred in messagesSeen: ${e}`)
      }
    }

    /**
     * @author Antonio Vazquez
     * @description get conversation asigned to user
     * @createdDate 07-01-26
     * @param {*} userId
     * @param {*} token
     * @return {*}
     * @memberof UsageService
     */
    async getChatsByUserId(userId, token) {
      try {
        const response = await axios.get(
          `${this.apiUrl}/get-chats-by-userId?userId=${userId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.statusCode !== 200) {
          throw new Error(response.data.message)
        }
        return response.data.data
      } catch (e) {
        console.error(`An error occurred in getChatsByUserId: ${e.message}`)
      }
    }

    /**
     * @author Antonio Vazquez
     * @description get categories and users to assign the conversation
     * @date 20-01-26
     * @param {*} clientId client id
     * @param {*} token token
     * @return {Object}
     * @memberof UsageService
     */
    async getCategoriesUsers(clientId, token) {
      try {
        const response = await axios.get(
          `${this.apiUrl}/categories-users?clientId=${clientId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.statusCode !== 200) {
          throw new Error(response.data.message)
        }
        return response.data.data
      } catch (e) {
        console.error(`An error occurred in getCategoriesUsers: ${e.message}`)
      }
    }

    /**
     * @author Antonio Vazquez
     * @description assign conversation to a category or user
     * @date 20-01-26
     * @param {*} conversationId conversation id
     * @param {*} categoryId category id
     * @param {*} userId user id
     * @param {*} token token
     * @return {Object}
     * @memberof UsageService
     */
    async putAssignConversation(conversationId, categoryId, userId, token) {
      try {
        const response = await axios.put(
          `${this.apiUrl}/assign-conversation`,
          {
            conversationId,
            categoryId,
            userId
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.statusCode !== 200) {
          throw new Error(response.data.message)
        }
        return response.data.data
      } catch (e) {
        console.error(`An error occurred in getCategoriesUsers: ${e.message}`)
      }
    }
}

export { UsageService as default }